import { createRoute, Outlet, redirect } from "@tanstack/react-router"

import { TaskNotFoundException } from "@/lib/client/types.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { initTaskLocation, Navigator } from "@/routes/Navigator.ts"
import { ProjectRootRoute } from "@/routes/projects/projectsRoutes.tsx"
import { TaskListModel } from "@/routes/tasks/TaskListScene/TaskListModel.ts"
import { TaskListScene } from "@/routes/tasks/TaskListScene/TaskListScene.tsx"
import { TaskNewScene } from "@/routes/tasks/TaskNewScene/TaskNewScene.tsx"
import { taskDetailsKey, taskKey } from "@/routes/tasks/tasksHooks.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { isTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"
import { TaskViewScene } from "@/routes/tasks/TaskViewScene/TaskViewScene.tsx"

import type { TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { TaskListId } from "@/routes/tasks/TaskListScene/TaskListId.ts"
import type { TaskTabId } from "@/routes/tasks/TaskViewScene/TaskTabId.ts"


export const TasksRoute = createRoute({
  path: "t",
  component: () => <Outlet />,

  getParentRoute: () => ProjectRootRoute,
})

export const TasksRouteIndex = createRoute({
  path: "/",
  component: () => <TaskListScene />,

  beforeLoad: ({ context }) => {
    if (context.project.initProjectTaskId) {
      throw redirect(initTaskLocation(context.orgId, context.project.key, context.project.initProjectTaskId))
    }
    return context
  },

  validateSearch: (search: Record<string, unknown>): TaskListSearch => {
    const list = search.list as TaskListId
    const page = Number(search.page) || undefined
    const successful = typeof search.successful !== "undefined" ? search.successful === true : undefined

    return {
      list,
      page,
      successful
    }
  },

  loaderDeps: ({ search }) => {
    return {
      page: Number(search.page) || 0,
      list: search.list,
      successful: search.successful === true
    }
  },

  loader: async ({ context, deps }) => {
    const project = context.project

    const model = new TaskListModel(context, {
      project: project,
      page: deps.page,
      type: deps.list,
      successful: deps.successful
    })

    await withGlobalLoader(context, model.awaitReady())

    return { model }
  },

  getParentRoute: () => TasksRoute,
})

export const TasksRouteNewTask = createRoute({
  path: "new",
  component: () => <TaskNewScene />,

  beforeLoad: ({ context }) => {
    if (context.project.initProjectTaskId) {
      throw redirect(initTaskLocation(context.orgId, context.project.key, context.project.initProjectTaskId))
    }
    return context
  },

  validateSearch: (search: Record<string, unknown>): TaskNewSearch => {
    const onboarding = search.onboarding as boolean
    const fromProjectList = search.fromProjectList as boolean
    const fromUserProfile = search.fromUserProfile as string
    const fromTaskList = search.fromTaskList as string
    const fromTaskView = search.fromTaskView as string

    return {
      onboarding,
      fromProjectList,
      fromUserProfile,
      fromTaskList,
      fromTaskView
    }
  },

  getParentRoute: () => TasksRoute,
})

export const TasksRouteViewTask = createRoute({
  path: "$taskId",
  component: () => <TaskViewScene />,

  beforeLoad: async ({ context, params, search }) => {
    if (context.project.initProjectTaskId && params.taskId !== context.project.initProjectTaskId) {
      throw redirect(initTaskLocation(context.orgId, context.project.key, context.project.initProjectTaskId))
    }

    let taskWithDetails
    try {
      taskWithDetails = await withGlobalLoader(context, context.queries.taskDetails(params.taskId))
    } catch (error) {
      if (error instanceof TaskNotFoundException) {
        throw new TaskNotFoundException(error.message, context.orgId, context.project.key.key)
      }
      throw error
    }

    if (context.project.key.key !== taskWithDetails.task.projectKey.key && taskWithDetails.task.projectKey) {
      throw redirect(Navigator.task(context.orgId, taskWithDetails.task.projectKey, params.taskId, search))
    }

    const config = taskWithDetails.runtimeConfig

    if (taskWithDetails.task.initRuntimeConfigState) {
      if (config?.previewCommands || config?.beforeCommand || taskWithDetails.task.initRuntimeConfigState.guidelinesGenerated) {
        if (search.view == TaskViewId.Chat) {
          throw redirect(initTaskLocation(context.orgId, context.project.key, params.taskId as TID, TaskViewId.Full))
        }
      } else if (search.view != TaskViewId.Chat) {
        throw redirect(initTaskLocation(context.orgId, context.project.key, params.taskId as TID, TaskViewId.Chat))
      }
    }

    return { taskWithDetails }
  },

  onLeave: ({ context, params }) => {
    void context.queryClient.invalidateQueries(taskKey(params.taskId))
    void context.queryClient.invalidateQueries(taskDetailsKey(params.taskId))
  },

  loader: ({ context }) => {
    return { taskWithDetails: context.taskWithDetails }
  },

  validateSearch: (search: Record<string, unknown>): TaskViewSearch => {
    const tab = search.tab as string
    const tabId = isTabId(tab) ? tab : undefined
    const view = search.view as TaskViewId

    return {
      tab: tabId,
      view: view,
      checkpoint: search.checkpoint ? search.checkpoint as string : undefined,
      message: typeof search.message === "string" ? search.message : undefined,
    }
  },

  getParentRoute: () => TasksRoute,
})

type TaskListSearch = {
  list?: TaskListId
  page?: number,
  successful?: boolean
}

type TaskViewSearch = {
  tab?: TaskTabId
  view?: TaskViewId
  checkpoint?: string
  message?: string
}

type TaskNewSearch = {
  onboarding?: boolean
  fromProjectList?: boolean
  fromUserProfile?: string
  fromTaskList?: string
  fromTaskView?: string
}